import { z } from "zod";
import { documentTaskTypeKey } from "../drizzle/taskTypes.js";
import { isStableFileUrl } from "../stable-file-url/stable-file-url.js";
import { attachmentSchema, categorySchema, entitySchema } from "./heidelbot-entities.js";
// --- Email task creation schema ---
// Also the display/category filename the frontend uses for the synthetic email-body slide.
export const EMAIL_BODY_FILENAME = "email.pdf";
/**
 * Heidelbot inbound contract (POST /tasks/email).
 * The check step reads `email.bodyPdfUrl` and the top-level `categories` as a pair; the rest of
 * `email` is display metadata.
 */
export const createEmailTaskSchema = z
    .object({
    type: z.enum(documentTaskTypeKey).meta({
        description: "Task type determining workflow routing",
        example: "cost_estimate",
    }),
    priority: z.number().int().positive().default(1).meta({
        description: "Task priority (higher = more important). Defaults to 1 if omitted.",
        example: 5,
    }),
    categories: z.array(categorySchema).meta({ description: "Document-level categories with confidence" }),
    entities: z.array(entitySchema).meta({ description: "Top-level extracted entities with confidence" }),
    email: z
        .object({
        gmailId: z
            .string()
            .min(1)
            .meta({
            description: "Message ID of the e-mail processed from the Gmail inbox. It must name a mail in the message-manager. " +
                "ePort claims that mail before it creates the task, so an unknown gmailId returns 404 and creates no task.",
        }),
        sender: z.email({ pattern: z.regexes.html5Email }).meta({ description: "E-mail sender address" }),
        receiver: z.email({ pattern: z.regexes.html5Email }).meta({ description: "E-mail receiver address" }),
        subject: z.string().min(1).max(998).meta({ description: "E-mail subject (Gmail max 998 chars)" }),
        summary: z.string().optional().meta({ description: "AI-generated summary of the e-mail" }),
        conversationSummary: z.string().optional().meta({ description: "AI-generated summary of the e-mail conversation" }),
        bodyPdfUrl: z.url().refine(isStableFileUrl, { message: "bodyPdfUrl must be an s3-adapter Stable File URL" }).meta({
            description: "Stable File URL of the PDF-rendered e-mail body, resolved server-side at view time. Presigned S3 URLs are rejected.",
        }),
    })
        .meta({ description: "The source e-mail this task was created from" }),
    attachments: z
        .array(attachmentSchema)
        .default([])
        .meta({
        description: "E-mail attachments with extracted data. An invoice task needs at least one attachment whose highest-confidence category is DOCUMENT. " +
            "Without one, the request returns 400 and creates no task.",
    }),
})
    .describe("Request body for creating an e-mail-based task (from Heidelbot)");
/** Narrowed view of {@link createEmailTaskSchema}'s `email` shape, for callers that only need `gmailId`. */
export const gmailIdOnlySchema = z.object({ email: createEmailTaskSchema.shape.email.pick({ gmailId: true }) });
